import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Input } from "../../components/ui/input";
import {
  Search, ShieldCheck, ShieldAlert, ShieldQuestion,
  Clock, Sparkles, ExternalLink,
} from "lucide-react";
import AdminAccount360Drawer from "../../components/AdminAccount360Drawer";
import { toast } from "sonner";

// Admin · Users — table of every account on the platform with a KYC review
// drawer. The owner-KYC submission (legal name, DOB, last-4 SSN — hashed at
// rest) is captured at sign-up; admins can review and flip the status to
// verified / rejected here. The actual SSN bcrypt hash never crosses the
// network; only the redacted summary is shown.

const STATUS_TONES = {
  verified: { Icon: ShieldCheck,    cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Verified" },
  pending:  { Icon: Clock,          cls: "bg-amber-50 text-amber-800 border-amber-200",       label: "Pending" },
  rejected: { Icon: ShieldAlert,    cls: "bg-rose-50 text-rose-700 border-rose-200",          label: "Rejected" },
  none:     { Icon: ShieldQuestion, cls: "bg-secondary text-foreground/55 border-border",     label: "—" },
};

function statusKey(u) {
  if (!u.owner_kyc) return "none";
  return u.owner_kyc.verification_status || "pending";
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");  // all | pending | verified | rejected
  const [selected, setSelected] = useState(null);

  const load = () =>
    api.get("/admin/users").then(r => setUsers(r.data));

  const launchAsUser = async (u) => {
    const reason = window.prompt(
      `Launch ${u.email}'s portal in a new tab?\n\nMode: WRITE · 30 minutes · audit-logged.\nReason (min 3 chars):`,
      "Support — investigating issue"
    );
    if (!reason || reason.trim().length < 3) return;
    try {
      const r = await api.post(`/admin/portal-access/launch/${u.id}`, {
        mode: "write", minutes: 30, reason: reason.trim(),
      });
      window.open(r.data.launch_url, "_blank", "noopener");
      toast.success(`Launched ${u.email}'s portal · expires ${new Date(r.data.expires_at).toLocaleTimeString()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Launch failed");
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { all: users.length, pending: 0, verified: 0, rejected: 0, none: 0 };
    users.forEach(u => { c[statusKey(u)] = (c[statusKey(u)] || 0) + 1; });
    return c;
  }, [users]);

  const visible = useMemo(() => {
    let rows = users;
    if (filter !== "all") rows = rows.filter(u => statusKey(u) === filter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      rows = rows.filter(u =>
        (u.name || "").toLowerCase().includes(needle) ||
        (u.email || "").toLowerCase().includes(needle) ||
        (u.company || "").toLowerCase().includes(needle) ||
        (u.owner_kyc?.legal_name || "").toLowerCase().includes(needle)
      );
    }
    return rows;
  }, [users, q, filter]);

  return (
    <>
      <PageHeader
        title="Users · Account 360"
        subtitle={`${users.length} accounts · ${counts.pending || 0} pending identity review · click any row for full settings audit`}
      />

      <div className="p-8 space-y-5" data-testid="admin-users">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 justify-between">
          <div className="flex flex-wrap gap-1.5">
            {[
              { k: "all",      l: "All",            n: counts.all },
              { k: "pending",  l: "Pending review", n: counts.pending },
              { k: "verified", l: "Verified",       n: counts.verified },
              { k: "rejected", l: "Rejected",       n: counts.rejected },
              { k: "none",     l: "Legacy (no KYC)", n: counts.none },
            ].map(t => {
              const active = filter === t.k;
              return (
                <button key={t.k} onClick={() => setFilter(t.k)}
                  data-testid={`filter-${t.k}`}
                  className={`text-[12px] rounded-full px-3.5 py-1.5 border transition-all ${
                    active
                      ? "bg-foreground text-background border-foreground"
                      : "bg-white border-border text-foreground/65 hover:border-foreground/40"
                  }`}>
                  {t.l} <span className={`ml-1 mono text-[10.5px] ${active ? "text-accent" : "text-foreground/45"}`}>{t.n}</span>
                </button>
              );
            })}
          </div>
          <div className="relative w-64">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search name, email, company, owner…"
              className="pl-9 h-9 rounded-full bg-white border-border text-[12.5px]"
              data-testid="user-search"
            />
          </div>
        </div>

        {/* Table */}
        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Email · Phone</th>
                <th className="px-5 py-3">Company</th>
                <th className="px-5 py-3">Role</th>
                <th className="px-5 py-3">Identity check</th>
                <th className="px-5 py-3">Joined</th>
                <th className="px-5 py-3"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {visible.map(u => {
                const k = statusKey(u);
                const tone = STATUS_TONES[k];
                return (
                  <tr key={u.id} className="hover:bg-secondary/30 cursor-pointer transition-colors"
                    onClick={() => setSelected(u)}
                    data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3.5 font-medium">{u.name || "—"}</td>
                    <td className="px-5 py-3.5 text-foreground/70">
                      <div className="mono text-[12.5px]">{u.email}</div>
                      {u.phone && <div className="mono text-[11px] text-foreground/45 mt-0.5">{u.phone}</div>}
                    </td>
                    <td className="px-5 py-3.5">{u.company || "—"}</td>
                    <td className="px-5 py-3.5">
                      <span className="chip bg-foreground text-background text-[10px]">{u.role}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`chip border text-[10.5px] inline-flex items-center gap-1 ${tone.cls}`}>
                        <tone.Icon className="w-3 h-3"/>{tone.label}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-[11.5px] text-foreground/55 mono">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {u.role !== "admin" && (
                          <button
                            onClick={(e) => { e.stopPropagation(); launchAsUser(u); }}
                            className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-widest text-violet-700 hover:text-violet-900"
                            data-testid={`user-launch-${u.id}`}
                            title="Open this user's portal in a new tab (write impersonation, 30 min)">
                            <ExternalLink className="w-3 h-3"/>Open as
                          </button>
                        )}
                        <span className="inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-widest text-foreground/60 hover:text-foreground">
                          <Sparkles className="w-3 h-3 text-accent"/>Account 360 →
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-foreground/45">No users match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AdminAccount360Drawer
        user={selected}
        onClose={() => setSelected(null)}
        onKycUpdated={(fresh) => {
          setUsers(arr => arr.map(x => x.id === fresh.id ? fresh : x));
          setSelected(fresh);
        }}
        onDeleted={(uid) => {
          setUsers(arr => arr.filter(x => x.id !== uid));
        }}
      />
    </>
  );
}
